/**
 * Reset App Cache — small Settings card that lets the venue admin
 * force-purge the service worker + all caches on demand. Complements the
 * automatic weekly reset in registerSW.js — useful when a client calls in
 * saying "the fix didn't land" and we need them to pick up the latest
 * build immediately without waiting for the timer.
 */
import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { RefreshCw, Trash2, Loader2 } from 'lucide-react';
import { forceResetAppCache } from '../utils/registerSW';

const WEEKLY_RESET_KEY = "spin:sw:lastCacheClearAt:v2";
const WEEK_MS = 12 * 3600 * 1000; // 12h auto-reset cycle

const fmt = (ms) => {
  if (!ms) return "never";
  const d = new Date(ms);
  const diff = Date.now() - ms;
  const hrs = Math.floor(diff / 3600000);
  if (hrs < 1) return `${Math.max(0, Math.floor(diff / 60000))} min ago`;
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago (${d.toLocaleDateString()})`;
};

export const ResetCacheCard = () => {
  const [lastReset, setLastReset] = useState(0);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(WEEKLY_RESET_KEY);
      setLastReset(raw ? parseInt(raw, 10) || 0 : 0);
    } catch { /* noop */ }
  }, []);

  const dueIn = lastReset ? Math.max(0, (lastReset + WEEK_MS) - Date.now()) : 0;
  const dueLabel = dueIn > 0
    ? (dueIn > 3600000
        ? `in ~${Math.ceil(dueIn / 3600000)} hour${Math.ceil(dueIn / 3600000) === 1 ? '' : 's'}`
        : `in ~${Math.ceil(dueIn / 60000)} min`)
    : "on next idle moment";

  const doReset = async () => {
    if (!window.confirm("Clear cached app assets and reload? Any unsaved input will be lost.")) return;
    setClearing(true);
    try {
      await forceResetAppCache();
    } catch (err) {
      setClearing(false);
      console.warn("[ResetCache] failed:", err);
    }
  };

  return (
    <div
      className="bg-white/[0.03] border border-white/10 rounded-2xl lg:rounded-3xl p-5 lg:p-6 backdrop-blur-xl"
      data-testid="reset-cache-card"
    >
      <div className="flex items-center gap-2 mb-4">
        <RefreshCw className="w-5 h-5 text-[#FF00FF]" />
        <h3 className="text-sm font-extrabold text-white uppercase tracking-[0.18em]" style={{ fontFamily: 'Outfit' }}>
          App Cache
        </h3>
      </div>

      <div className="p-3 rounded-xl bg-black/30 border border-white/5 mb-4">
        <p className="text-white text-sm font-semibold mb-1">Automatic 12-hour reset</p>
        <p className="text-gray-500 text-[11px] leading-relaxed">
          The app auto-clears cached JS + assets every ~12 hours so venue
          devices don&apos;t accumulate stale session state that slows down
          song transitions. The reset waits for a natural gap between songs
          (never reloads mid-track). Last reset:{' '}
          <span className="text-white/80 font-['JetBrains_Mono']">{fmt(lastReset)}</span>
          . Next: <span className="text-white/80 font-['JetBrains_Mono']">{dueLabel}</span>.
        </p>
      </div>

      <div className="p-3 rounded-xl bg-black/30 border border-white/5">
        <p className="text-white text-sm font-semibold mb-1">Force reset now</p>
        <p className="text-gray-500 text-[11px] mb-3 leading-relaxed">
          Use this if you were told a fix has shipped but the app still
          behaves the old way. Wipes the service worker + all cached assets
          + reloads the page.
        </p>
        <Button
          onClick={doReset}
          disabled={clearing}
          className="h-10 bg-[#FF00FF]/15 hover:bg-[#FF00FF]/25 border border-[#FF00FF]/50 text-[#FF00FF] rounded-lg px-4 font-semibold text-xs uppercase tracking-[0.2em] flex items-center gap-2 disabled:opacity-50"
          data-testid="reset-cache-button"
        >
          {clearing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" strokeWidth={2} />}
          {clearing ? "Clearing…" : "Reset App Cache"}
        </Button>
      </div>
    </div>
  );
};

export default ResetCacheCard;
